import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";

export type A11yMode = "auto" | "on" | "off";

interface AccessibilityContextValue {
  mode: A11yMode;
  isOn: boolean;
  setMode: (mode: A11yMode) => void;
}

const STORAGE_KEY = "ecocupid.a11yMode";
const CLASS_NAME = "cb";

const AccessibilityContext = createContext<AccessibilityContextValue | null>(null);

const readStoredMode = (): A11yMode => {
  if (typeof window === "undefined") return "auto";
  const value = window.localStorage.getItem(STORAGE_KEY);
  return value === "on" || value === "off" || value === "auto" ? value : "auto";
};

const detectPreferred = (): boolean => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return (
    window.matchMedia("(prefers-contrast: more)").matches ||
    window.matchMedia("(forced-colors: active)").matches
  );
};

export const AccessibilityProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<A11yMode>(() => readStoredMode());
  const [systemOn, setSystemOn] = useState<boolean>(() => detectPreferred());

  // Watch system preference changes for "auto" mode.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const queries = [
      window.matchMedia("(prefers-contrast: more)"),
      window.matchMedia("(forced-colors: active)"),
    ];
    const handler = () => setSystemOn(detectPreferred());
    queries.forEach((q) => q.addEventListener?.("change", handler));
    return () => queries.forEach((q) => q.removeEventListener?.("change", handler));
  }, []);

  const isOn = mode === "on" || (mode === "auto" && systemOn);

  // Apply / remove the .cb class on <html>.
  useEffect(() => {
    const root = document.documentElement;
    if (isOn) {
      root.classList.add(CLASS_NAME);
    } else {
      root.classList.remove(CLASS_NAME);
    }
  }, [isOn]);

  const setMode = useCallback((next: A11yMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore storage failures (private mode, etc.)
    }
  }, []);

  return (
    <AccessibilityContext.Provider value={{ mode, isOn, setMode }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = (): AccessibilityContextValue => {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) {
    // Safe fallback so isolated previews don't crash.
    return { mode: "auto", isOn: false, setMode: () => {} };
  }
  return ctx;
};
